import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import moment from "moment";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import useConstants from "../../hooks/useConstants";
import { getLocations } from "../../api/locations";
import { createBidRequest, updateBidRequestStatus } from "../../api/bids";
import { maxOrderSize } from "../../utils/status";

// Mirrors ordr-flutter bid_request -> bid_request_step_two -> confirm_bid_request flow,
// collapsed into a single form + review step for the web.
export default function NewBidRequest() {
	const navigate = useNavigate();
	const { constants, loading: constantsLoading } = useConstants();
	const [locations, setLocations] = useState([]);
	const [loadingLocations, setLoadingLocations] = useState(true);
	const [step, setStep] = useState(1);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		(async () => {
			const res = await getLocations();
			if (res && !res.error) setLocations(res.data || []);
			setLoadingLocations(false);
		})();
	}, []);

	const formik = useFormik({
		initialValues: {
			locationId: "",
			productId: "",
			orderSize: "",
			deliveryDate: null,
			deliveryTime: "",
			otherNotes: "",
		},
		validationSchema: yup.object({
			locationId: yup.string().required("Select a location"),
			productId: yup.string().required("Select a product"),
			orderSize: yup
				.number()
				.typeError("Enter a number")
				.integer("Enter a whole number of gallons")
				.min(1, "Order size can't be 0")
				.required("Required")
				.test("max-order-size", function (value) {
					const product = constants.locationProducts.find((p) => String(p.id) === String(this.parent.productId));
					if (!product || !value) return true;
					const max = maxOrderSize(product);
					return value <= max || this.createError({ message: `Order size should not be greater than ${max.toLocaleString()} gal` });
				}),
			deliveryDate: yup.date().nullable().required("Select a delivery date"),
			deliveryTime: yup.string().required("Select a delivery window"),
		}),
		onSubmit: () => setStep(2),
	});

	const confirmSubmit = async () => {
		setSubmitting(true);
		const res = await createBidRequest({
			locationId: formik.values.locationId,
			productId: formik.values.productId,
			orderSize: String(formik.values.orderSize),
			deliveryDate: moment(formik.values.deliveryDate).format("YYYY-MM-DD"),
			deliveryTime: formik.values.deliveryTime,
			otherNotes: formik.values.otherNotes,
		});
		if (res && !res.error) {
			// Creating the bid only leaves it in "Bid Created" (state 1), which no seller query
			// matches — it has to be explicitly approved (state 2) before sellers are notified
			// and matched to it (mirrors ordr-flutter's confirm_bid_request_bloc, which fires
			// this same call right after creation). Skipping it is why bids never reached sellers.
			const approveRes = await updateBidRequestStatus(res.data?.bidRequestId, { bidRequestState: 2 });
			setSubmitting(false);
			if (approveRes && !approveRes.error) {
				toast.success("Bid request submitted!");
				navigate("/orders", { state: { tab: "bidRequest" } });
			} else {
				toast.error(approveRes?.msg || "Bid request was created but could not be sent to sellers. Please contact support.");
			}
		} else {
			setSubmitting(false);
			toast.error(res?.msg || "Could not submit bid request");
		}
	};

	if (constantsLoading || loadingLocations) return <Loader />;

	const selectedLocation = locations.find((l) => l.id === formik.values.locationId);
	const selectedProduct = constants.locationProducts.find((p) => String(p.id) === String(formik.values.productId));
	const selectedTiming = constants.deliveryTiming.find((t) => t.value === formik.values.deliveryTime);

	if (locations.length === 0) {
		return (
			<div>
				<PageHeader back title="New bid request" />
				<div className="card p-4 text-center">
					<div className="mb-3">You need at least one location before requesting a bid.</div>
					<Link to="/locations/new" className="btn-accent-solid" style={{ width: 200, margin: "0 auto" }}>
						Add a location
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div>
			<PageHeader back title="New bid request" subtitle="Tell sellers what you need — they'll compete to give you the best price." />
			<div className="stepper mb-4">
				{["Order details", "Review & submit"].map((label, i) => (
					<div key={label} className={`stepper-step${step === i + 1 ? " active" : ""}${step > i + 1 ? " done" : ""}`}>
						<span className="stepper-num">{i + 1}</span>
						<span>{label}</span>
					</div>
				))}
			</div>

			{step === 1 && (
				<form className="card p-4" style={{ maxWidth: 640 }} onSubmit={formik.handleSubmit}>
					<div className="mb-3">
						<label className="form-label">Delivery location</label>
						<select className="form-select" name="locationId" value={formik.values.locationId} onChange={formik.handleChange}>
							<option value="">Select location</option>
							{locations.map((l) => (
								<option key={l.id} value={l.id}>{l.locationName}</option>
							))}
						</select>
						{formik.touched.locationId && formik.errors.locationId ? <div className="text-danger small">{formik.errors.locationId}</div> : null}
					</div>
					<div className="mb-3">
						<label className="form-label">Product</label>
						<select className="form-select" name="productId" value={formik.values.productId} onChange={formik.handleChange}>
							<option value="">Select product</option>
							{constants.locationProducts.map((opt) => (
								<option key={opt.id} value={opt.id}>{opt.Title}</option>
							))}
						</select>
						{formik.touched.productId && formik.errors.productId ? <div className="text-danger small">{formik.errors.productId}</div> : null}
					</div>
					<div className="mb-3">
						<label className="form-label">Order size (gallons){selectedProduct ? <span className="text-muted small"> · max {maxOrderSize(selectedProduct).toLocaleString()}</span> : null}</label>
						<input className="form-control" name="orderSize" value={formik.values.orderSize} onChange={formik.handleChange} />
						{formik.touched.orderSize && formik.errors.orderSize ? <div className="text-danger small">{formik.errors.orderSize}</div> : null}
					</div>
					<div className="row g-2 mb-3">
						<div className="col-6">
							<label className="form-label">Delivery date</label>
							<div>
								<DatePicker
									className="form-control"
									selected={formik.values.deliveryDate}
									minDate={new Date()}
									onChange={(date) => formik.setFieldValue("deliveryDate", date)}
									placeholderText="Select date"
								/>
							</div>
							{formik.touched.deliveryDate && formik.errors.deliveryDate ? <div className="text-danger small">{formik.errors.deliveryDate}</div> : null}
						</div>
						<div className="col-6">
							<label className="form-label">Delivery window</label>
							<select className="form-select" name="deliveryTime" value={formik.values.deliveryTime} onChange={formik.handleChange}>
								<option value="">Select window</option>
								{constants.deliveryTiming.map((opt) => (
									<option key={opt.id} value={opt.value}>{opt.Title}</option>
								))}
							</select>
							{formik.touched.deliveryTime && formik.errors.deliveryTime ? <div className="text-danger small">{formik.errors.deliveryTime}</div> : null}
						</div>
					</div>
					<div className="mb-4">
						<label className="form-label">Other notes (optional)</label>
						<textarea className="form-control" rows={3} name="otherNotes" value={formik.values.otherNotes} onChange={formik.handleChange} />
					</div>
					<button type="submit" className="btn-accent-solid" style={{ width: 200 }}>
						Review bid request
					</button>
				</form>
			)}

			{step === 2 && (
				<div className="card p-4" style={{ maxWidth: 640 }}>
					<Row label="Location" value={selectedLocation?.locationName} />
					<Row label="Address" value={selectedLocation ? `${selectedLocation.streetAddress}, ${selectedLocation.city}, ${selectedLocation.state}` : ""} />
					<Row label="Product" value={selectedProduct?.Title} />
					<Row label="Order size" value={`${formik.values.orderSize} gal`} />
					<Row label="Delivery date" value={moment(formik.values.deliveryDate).format("MMM D, YYYY")} />
					<Row label="Delivery window" value={selectedTiming?.Title} />
					{formik.values.otherNotes ? <Row label="Notes" value={formik.values.otherNotes} /> : null}
					<div className="d-flex gap-2 mt-4">
						<button className="btn-accent-solid" disabled={submitting} onClick={confirmSubmit}>
							{submitting ? "Submitting..." : "Confirm & submit"}
						</button>
						<button className="btn-outline" onClick={() => setStep(1)}>
							Back
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

function Row({ label, value }) {
	return (
		<div className="d-flex justify-content-between border-bottom py-2">
			<span className="text-muted">{label}</span>
			<span className="fw-semibold text-end">{value || "-"}</span>
		</div>
	);
}
